import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Box, Typography, Paper, Chip, Stack, Grid, TextField, MenuItem, FormControl, InputLabel, Select, Alert } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { formatarParaReais } from "../../services/utilitarios";
import { StatusPagamento } from "../../services/tipos";
import { useState, useMemo, useEffect } from "react";

interface ProcessamentoLotePreviewDto {
    nomeLocatario: string;
    quantidade: number;
    valorTotal: number;
}

// Detalhe item a item (pagamento individual) usado na etapa de confirmação.
interface PagamentoLoteItemDto {
    id: string;
    nomeLocatario: string;
    descricao?: string;
    vencimento?: string | null;
    valorAtual: number;
}

interface ModalAjusteValorLoteProps {
    open: boolean;
    onClose: () => void;
    onConfirm: (tipoAjuste: string, valorAjuste: number, confirmarBaixa: boolean) => void;
    dados: ProcessamentoLotePreviewDto[];
    // Itens individuais (opcional). Quando fornecido, a etapa de confirmação lista cada pagamento
    // com valor atual → novo. Sem ele, a confirmação cai no resumo agregado por locatário.
    itens?: PagamentoLoteItemDto[];
    filtros: {
        dataInicio: Date | null;
        dataFim: Date | null;
        status: StatusPagamento;
        idLocacao: string;
    };
    carregando: boolean;
    // Quando true, exibe a escolha "apenas editar valor" vs "editar valor e confirmar pagamento".
    mostrarOpcaoBaixa?: boolean;
}

const arredondar2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

// Cor da identidade do sistema.
const COR_MARCA = '#1F467E';
const COR_ALERTA = '#FFC04D'; // âmbar para destacar avisos sobre o fundo da marca (bom contraste).

export function ModalAjusteValorLote({ open, onClose, onConfirm, dados, itens, filtros, carregando, mostrarOpcaoBaixa = false }: ModalAjusteValorLoteProps) {
    const [tipoAjuste, setTipoAjuste] = useState<'fixo' | 'acrescimo' | 'percentual'>('fixo');
    const [valorAjuste, setValorAjuste] = useState<string>('');
    const [confirmarBaixa, setConfirmarBaixa] = useState<boolean>(false);
    // Etapa 2: revisão/confirmação exibida antes da efetivação de fato.
    const [confirmando, setConfirmando] = useState<boolean>(false);

    // Reinicia o fluxo sempre que o modal abre.
    useEffect(() => {
        if (open) setConfirmando(false);
    }, [open]);

    const valorNumerico = parseFloat(valorAjuste.replace(',', '.'));
    const temItensDetalhados = !!itens && itens.length > 0;

    // Calcula o novo valor de UM pagamento (mesma fórmula do backend, arredondando por item).
    const calcularNovoValorItem = (valorAtual: number) => {
        const valor = isNaN(valorNumerico) ? 0 : valorNumerico;
        let nv: number;
        if (tipoAjuste === 'fixo') nv = valor;
        else if (tipoAjuste === 'acrescimo') nv = valorAtual + valor;
        else nv = valorAtual * (1 + (valor / 100));
        return arredondar2(nv);
    };

    // ---- Etapa 1: prévia agregada por locatário ----
    const calcularNovoTotalAgregado = (item: ProcessamentoLotePreviewDto) => {
        const valor = isNaN(valorNumerico) ? 0 : valorNumerico;
        if (tipoAjuste === 'fixo') return item.quantidade * valor;
        if (tipoAjuste === 'acrescimo') return item.valorTotal + (item.quantidade * valor);
        if (tipoAjuste === 'percentual') return item.valorTotal * (1 + (valor / 100));
        return item.valorTotal;
    };

    const colunasAgregado: GridColDef[] = [
        { field: 'nomeLocatario', headerName: 'Locatário', flex: 1 },
        { field: 'quantidade', headerName: 'Qtd.', width: 80, align: 'center', headerAlign: 'center' },
        {
            field: 'valorTotal',
            headerName: 'Atual',
            width: 120,
            renderCell: (params) => <span style={{ color: '#666' }}>{formatarParaReais(params.value)}</span>
        },
        {
            field: 'novoValorTotal',
            headerName: 'Novo (Estimado)',
            width: 130,
            renderCell: (params) => <span style={{ fontWeight: 'bold', color: COR_MARCA }}>{formatarParaReais(params.row.novoValorTotal)}</span>
        },
    ];

    const rowsAgregado = useMemo(() => dados.map((item, index) => ({
        id: index,
        ...item,
        novoValorTotal: calcularNovoTotalAgregado(item)
    })), [dados, tipoAjuste, valorAjuste]);

    const totalQtdAgregado = dados.reduce((acc, curr) => acc + curr.quantidade, 0);
    const totalAtualAgregado = dados.reduce((acc, curr) => acc + curr.valorTotal, 0);
    const totalNovoAgregado = rowsAgregado.reduce((acc, curr) => acc + curr.novoValorTotal, 0);

    // ---- Etapa 2: lista item a item ----
    const colunasItens: GridColDef[] = [
        { field: 'nomeLocatario', headerName: 'Locatário', flex: 1, minWidth: 140 },
        {
            field: 'vencimento',
            headerName: 'Vencimento',
            width: 110,
            renderCell: (params) => <span>{params.value ? new Date(params.value).toLocaleDateString('pt-BR') : '-'}</span>
        },
        {
            field: 'valorAtual',
            headerName: 'Atual',
            width: 120,
            renderCell: (params) => <span style={{ color: '#666' }}>{formatarParaReais(params.value)}</span>
        },
        {
            field: 'novoValor',
            headerName: 'Novo',
            width: 120,
            renderCell: (params) => (
                <span style={{ fontWeight: 'bold', color: params.row.invalido ? '#d32f2f' : COR_MARCA }}>
                    {formatarParaReais(params.value)}
                </span>
            )
        },
    ];

    const rowsItens = useMemo(() => (itens ?? []).map((it, index) => {
        const novo = calcularNovoValorItem(it.valorAtual);
        return {
            ...it,
            id: it.id ?? index,
            novoValor: novo,
            // backend trata valor <= 0 como falha (não aplica); sinalizamos em vermelho.
            invalido: novo <= 0
        };
    }), [itens, tipoAjuste, valorAjuste]);

    const totalQtdItens = rowsItens.length;
    const totalAtualItens = rowsItens.reduce((acc, curr) => acc + curr.valorAtual, 0);
    const totalNovoItens = rowsItens.reduce((acc, curr) => acc + curr.novoValor, 0);
    const qtdInvalidos = rowsItens.filter(r => r.invalido).length;

    // Totais exibidos na etapa de confirmação: usam o detalhe item a item quando disponível.
    const totalQtd = confirmando && temItensDetalhados ? totalQtdItens : totalQtdAgregado;
    const totalValorAtual = confirmando && temItensDetalhados ? totalAtualItens : totalAtualAgregado;
    const totalNovoValor = confirmando && temItensDetalhados ? totalNovoItens : totalNovoAgregado;

    const vaiBaixar = mostrarOpcaoBaixa && confirmarBaixa;

    const descricaoAjuste = (() => {
        if (isNaN(valorNumerico)) return '';
        if (tipoAjuste === 'fixo') return `Definir o valor de cada pagamento para ${formatarParaReais(valorNumerico)}`;
        if (tipoAjuste === 'acrescimo') return `${valorNumerico >= 0 ? 'Acrescentar' : 'Reduzir'} ${formatarParaReais(Math.abs(valorNumerico))} em cada pagamento`;
        return `Aplicar ${valorNumerico}% sobre o valor atual de cada pagamento`;
    })();

    // Etapa 1 → valida e avança para a revisão.
    const handleAvancar = () => {
        if (isNaN(valorNumerico)) return;
        if (tipoAjuste === 'fixo' && valorNumerico <= 0) return;
        if (tipoAjuste === 'percentual' && valorNumerico <= -100) return;
        setConfirmando(true);
    };

    // Etapa 2 → efetiva de fato.
    const handleConfirmarDefinitivo = () => {
        if (isNaN(valorNumerico)) return;
        onConfirm(tipoAjuste, valorNumerico, vaiBaixar);
    };

    const fecharModal = () => {
        setConfirmando(false);
        onClose();
    };

    const totaisPaper = (
        <Paper elevation={3} sx={{ mt: 3, p: 2, borderTop: `4px solid ${COR_MARCA}` }}>
            <Grid container alignItems="center" justifyContent="space-between">
                <Grid item>
                    <Typography variant="overline" display="block" color="text.secondary" lineHeight={1}>
                        ITENS
                    </Typography>
                    <Typography variant="h5" color="text.primary" fontWeight="bold">
                        {totalQtd}
                    </Typography>
                </Grid>
                <Grid item>
                    <Typography variant="overline" display="block" color="text.secondary" align="right" lineHeight={1}>
                        ATUAL
                    </Typography>
                    <Typography variant="h6" color="text.secondary" align="right">
                        {formatarParaReais(totalValorAtual)}
                    </Typography>
                </Grid>
                <Grid item>
                    <Typography variant="overline" display="block" color="text.secondary" align="right" lineHeight={1}>
                        {confirmando && temItensDetalhados ? 'NOVO TOTAL' : 'NOVO TOTAL (ESTIMADO)'}
                    </Typography>
                    <Typography variant="h5" fontWeight="bold" align="right" sx={{ color: COR_MARCA }}>
                        {formatarParaReais(totalNovoValor)}
                    </Typography>
                </Grid>
            </Grid>
        </Paper>
    );

    return (
        <Dialog
            open={open}
            onClose={fecharModal}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            maxWidth="md"
            fullWidth
        >
            <DialogTitle id="alert-dialog-title">
                {confirmando ? "Revisar e Confirmar Ajuste" : "Ajustar Valores em Lote"}
            </DialogTitle>
            <DialogContent>
                {!confirmando ? (
                    <>
                        <DialogContentText id="alert-dialog-description" component="div">
                            <Paper elevation={0} variant="outlined" sx={{ p: 2, mb: 3, bgcolor: '#f5f5f5' }}>
                                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: 'text.secondary' }}>
                                    FILTROS APLICADOS
                                </Typography>
                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ gap: 1 }}>
                                    {filtros.idLocacao && (
                                        <Chip label="Locação Selecionada" color="primary" size="small" variant="outlined" />
                                    )}
                                    {(filtros.dataInicio && filtros.dataFim) ? (
                                        <Chip
                                            label={`Período: ${filtros.dataInicio.toLocaleDateString('pt-BR')} até ${filtros.dataFim.toLocaleDateString('pt-BR')}`}
                                            variant="outlined"
                                            size="small"
                                        />
                                    ) : (
                                        <Chip label="Período: Todo o histórico" variant="outlined" size="small" />
                                    )}
                                </Stack>
                            </Paper>

                            <Grid container spacing={3} sx={{ mb: 3 }}>
                                <Grid item xs={12} md={6}>
                                    <FormControl fullWidth>
                                        <InputLabel>Tipo de Ajuste</InputLabel>
                                        <Select
                                            value={tipoAjuste}
                                            label="Tipo de Ajuste"
                                            onChange={(e) => setTipoAjuste(e.target.value as any)}
                                        >
                                            <MenuItem value="fixo">Definir Valor Fixo</MenuItem>
                                            <MenuItem value="acrescimo">Acrescentar Valor (R$)</MenuItem>
                                            <MenuItem value="percentual">Incrementar Porcentagem (%)</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        autoFocus
                                        label="Valor do Ajuste"
                                        fullWidth
                                        value={valorAjuste}
                                        onChange={(e) => setValorAjuste(e.target.value)}
                                        type="number"
                                        InputProps={{ inputProps: { step: "0.01" } }}
                                        helperText={
                                            tipoAjuste === 'fixo' ? "Novo valor para cada lançamento" :
                                            tipoAjuste === 'acrescimo' ? "Valor a somar em cada lançamento" :
                                            "Porcentagem a aplicar sobre o valor atual"
                                        }
                                    />
                                </Grid>
                            </Grid>

                            {mostrarOpcaoBaixa && (
                                <Box sx={{ mb: 3 }}>
                                    <FormControl fullWidth>
                                        <InputLabel>Ação</InputLabel>
                                        <Select
                                            value={confirmarBaixa ? 'baixar' : 'editar'}
                                            label="Ação"
                                            onChange={(e) => setConfirmarBaixa(e.target.value === 'baixar')}
                                        >
                                            <MenuItem value="editar">Apenas editar o valor</MenuItem>
                                            <MenuItem value="baixar">Editar o valor e confirmar pagamento (baixar)</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Box>
                            )}

                            <Typography variant="body2" sx={{ mb: 1 }}>
                                Prévia dos valores afetados (resumo por locatário):
                            </Typography>
                        </DialogContentText>

                        <div style={{ height: 300, width: '100%' }}>
                            <DataGrid
                                rows={rowsAgregado}
                                columns={colunasAgregado}
                                disableRowSelectionOnClick
                                hideFooter
                                density="compact"
                                sx={{ '& .MuiDataGrid-cell:focus': { outline: 'none' } }}
                            />
                        </div>

                        {totaisPaper}
                    </>
                ) : (
                    <>
                        {/* Aviso no TOPO, sempre visível ao entrar na confirmação. Usa a cor da marca
                            com texto branco para garantir contraste. */}
                        <Box sx={{ mb: 2, p: 2, borderRadius: 1, bgcolor: COR_MARCA, color: '#fff' }}>
                            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5, color: '#fff' }}>
                                Confira os valores antes de aplicar
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#fff' }}>{descricaoAjuste}.</Typography>
                            {vaiBaixar ? (
                                <Typography variant="body2" sx={{ mt: 1, color: COR_ALERTA, fontWeight: 'bold' }}>
                                    ⚠ Ação: editar o valor E confirmar o pagamento (baixar). Esta operação não pode ser desfeita automaticamente.
                                </Typography>
                            ) : (
                                <Typography variant="body2" sx={{ mt: 1, color: '#fff' }}>
                                    Ação: apenas editar o valor dos pagamentos.
                                </Typography>
                            )}
                        </Box>

                        {qtdInvalidos > 0 && (
                            <Alert severity="error" variant="outlined" sx={{ mb: 2 }}>
                                {qtdInvalidos} pagamento(s) ficariam com valor zero ou negativo (destacados em vermelho) e
                                <strong> não serão alterados</strong>. Revise o ajuste.
                            </Alert>
                        )}

                        <Typography variant="body2" sx={{ mb: 1 }}>
                            {temItensDetalhados
                                ? "Pagamentos que serão alterados (valor atual → novo):"
                                : "Resumo por locatário (valor atual → novo):"}
                        </Typography>

                        <div style={{ height: 340, width: '100%' }}>
                            {temItensDetalhados ? (
                                <DataGrid
                                    rows={rowsItens}
                                    columns={colunasItens}
                                    disableRowSelectionOnClick
                                    density="compact"
                                    pageSizeOptions={[10, 25, 50, 100]}
                                    initialState={{ pagination: { paginationModel: { pageSize: 10, page: 0 } } }}
                                    sx={{ '& .MuiDataGrid-cell:focus': { outline: 'none' } }}
                                />
                            ) : (
                                <DataGrid
                                    rows={rowsAgregado}
                                    columns={colunasAgregado}
                                    disableRowSelectionOnClick
                                    hideFooter
                                    density="compact"
                                    sx={{ '& .MuiDataGrid-cell:focus': { outline: 'none' } }}
                                />
                            )}
                        </div>

                        {totaisPaper}
                    </>
                )}
            </DialogContent>
            <DialogActions sx={{ p: 3 }}>
                {!confirmando ? (
                    <>
                        <Button onClick={fecharModal} color="inherit" variant="text">Cancelar</Button>
                        <Button
                            onClick={handleAvancar}
                            variant="contained"
                            disabled={!valorAjuste || carregando}
                            size="large"
                            sx={{ px: 4, bgcolor: COR_MARCA, '&:hover': { bgcolor: '#163866' } }}
                        >
                            Revisar
                        </Button>
                    </>
                ) : (
                    <>
                        <Button onClick={() => setConfirmando(false)} color="inherit" variant="text" disabled={carregando}>Voltar</Button>
                        <Button
                            onClick={handleConfirmarDefinitivo}
                            variant="contained"
                            disabled={carregando}
                            size="large"
                            sx={{ px: 4, bgcolor: COR_MARCA, '&:hover': { bgcolor: '#163866' } }}
                        >
                            {carregando ? "Processando..." : (vaiBaixar ? "Confirmar, editar e baixar" : "Confirmar e aplicar")}
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
}
